/**
 * The tasks a person sees, and a record's task rules said in words (step 9.7
 * of docs/plans/ledger.md; the Phase 9 brief, "task rules visible and in OS").
 *
 *   GET  /v1/tasks                                   the caller's open tasks: what a task rule or a
 *                                                    charge run's reminder raised for them
 *   POST /v1/tasks/:id/answer                        Done or Dismiss
 *   GET  /v1/records/:recordId/task-rules            a record's rules, each with its words
 *   POST /v1/records/:recordId/task-rules            add one (OS's `task_rule` card confirms through it)
 *   DELETE /v1/records/:recordId/task-rules/:ruleId  take one off
 *
 * `taskRuleWords` turns a rule kept by ids into the names a screen says, so
 * the settings section and OS's card read one rule the same way. Pure.
 */
import { titleFieldsOf, type TaskRule, type TaskRuleOn } from './task-rules';

/**
 * What raised a task: a record's task rule, the reminder the day before a charge run, somebody asking the owner to fix the
 * setup (L64.5), or the answer to such an ask: the row was added and the person's draft saved (`ask_answered`).
 */
export type MyTaskKind = 'task_rule' | 'charge_run' | 'ask_owner' | 'ask_answered';

/** What an `ask_owner` task asks (POST /v1/setup/fix-asks): the fix, which its Apply makes through POST /v1/setup/fixes. */
export type MyTaskAsk = {
  /** The finding to fix; null when the ask names no fix (a row to add, a grant). */
  readonly findingId: string | null;
  /** The fix in words, worked out when the list is read; null when it is no longer found (fixed already, or the setup moved). */
  readonly fix: string | null;
  /** Who asked, by name. */
  readonly askedBy: string | null;
  /** The person's own words, if they wrote any. */
  readonly note: string | null;
  /** The draft the ask is for, which Apply saves; null when it names none. */
  readonly entryId?: string | null;
  /** The row the draft waits for, when the ask is for one; Apply adds it (POST /v1/setup/fix-asks/:taskId/apply-row). */
  readonly row?: MyTaskAskRow | null;
};

/**
 * A row an ask wants added (L64.5): its list, its name as the person said it,
 * and its required choices, each filled with what it starts on (an Accounts
 * row's type from the money it is for: an expense for Money out). `fields`
 * lists those choices with the options the card may offer, so the owner can
 * change one before Apply.
 */
export type MyTaskAskRow = {
  readonly listId: string;
  readonly listName: string;
  readonly name: string;
  readonly values: Readonly<Record<string, string>>;
  readonly fields: readonly MyTaskAskRowField[];
};

export type MyTaskAskRowField = {
  readonly fieldId: string;
  readonly label: string;
  readonly options: readonly { readonly id: string; readonly label: string }[];
};

/** One open task, as the person it is for sees it. */
export type MyTask = {
  readonly id: string;
  readonly kind: MyTaskKind;
  /** The record it came from (a charge run's: its Charges record); null when that record is gone. */
  readonly recordId: string | null;
  readonly recordName: string | null;
  /** The entry it came from; null for a charge run's reminder. */
  readonly entryId: string | null;
  /** The task's words, as the rule said them when it was raised. */
  readonly message: string;
  /** The day it is due, `YYYY-MM-DD`. */
  readonly due: string;
  readonly createdAt: string;
  /** An `ask_owner` task's ask; absent on every other kind. The row's entry is the draft that waits on it. */
  readonly ask?: MyTaskAsk | null;
};

export type MyTaskListResponse = { readonly tasks: readonly MyTask[] };

export type TaskAnswer = 'done' | 'dismiss';

export type TaskAnswerResponse = { readonly answered: 'done' | 'dismissed' };

/** A rule as a person writes one: everything but its id, which the server gives it. */
export type TaskRuleWrite = Omit<TaskRule, 'id'>;

/** One condition, in names: the field it asks about, the op, and the values as a person reads them. */
export type TaskRuleConditionWords = { readonly field: string; readonly op: string; readonly values: readonly string[] };

/** A rule in the names a screen says. */
export type TaskRuleWords = {
  readonly on: TaskRuleOn;
  /** The title with each `{field id}` said as `{Label}`. */
  readonly title: string;
  readonly who:
    | { readonly kind: 'owner' }
    | { readonly kind: 'writer' }
    | { readonly kind: 'role'; readonly name: string }
    | { readonly kind: 'field'; readonly name: string };
  /** Joined by AND; each inner list by OR. Empty: every entry. */
  readonly when: readonly (readonly TaskRuleConditionWords[])[];
  /** Days after (below zero, before) a date field, or the day it fires (`from` null). */
  readonly due: { readonly from: string | null; readonly days: number };
};

export type TaskRuleRead = { readonly rule: TaskRule; readonly words: TaskRuleWords };

export type TaskRuleListResponse = {
  readonly recordId: string;
  readonly rules: readonly TaskRuleRead[];
  /** Whether the caller may add or take off a rule (`edit_structure`). */
  readonly mayChange: boolean;
};

export type TaskRuleAddRequest = { readonly rule: TaskRuleWrite };

/** One open task a task rule raised, as its routine page lists it (step 3.2 of docs/plans/routines.md). */
export type TaskRuleOpenTask = {
  readonly id: string;
  /** The entry it is about; null when that entry is gone. */
  readonly entryId: string | null;
  /** The task's words, as the task list says them: "Call Asha", or the rule's words when a field in it is hidden from the reader. */
  readonly message: string;
  /** The day it is due, `YYYY-MM-DD`. */
  readonly due: string;
  /** The person it is for; null when they are gone. */
  readonly assignee: string | null;
};

/** GET /v1/records/:recordId/task-rules/:ruleId/open: the rule's open tasks, whoever they are for. */
export type TaskRuleOpenResponse = { readonly recordId: string; readonly ruleId: string; readonly tasks: readonly TaskRuleOpenTask[] };

/** A field as the words need it. */
export type TaskWordField = {
  readonly id: string;
  readonly label: string;
  readonly options?: readonly { readonly id: string; readonly label?: string | null }[] | null;
};

/** The rule said in names: fields by label, a choice by its words, a role by its name. An unknown id is said as it is. */
export const taskRuleWords = (rule: TaskRuleWrite, fields: readonly TaskWordField[], roles: Readonly<Record<string, string>>): TaskRuleWords => {
  const fieldOf = (id: string): TaskWordField | undefined => fields.find((one) => one.id.toLowerCase() === id.toLowerCase());
  const label = (id: string): string => fieldOf(id)?.label ?? id;
  const value = (fieldId: string, held: string | number): string => {
    if (typeof held === 'number') return String(held);
    const option = (fieldOf(fieldId)?.options ?? []).find((one) => one.id === held);
    return option?.label ?? held;
  };
  let title = rule.title;
  for (const named of titleFieldsOf(rule.title)) title = title.split(`{${named}}`).join(`{${label(named)}}`);
  const who: TaskRuleWords['who'] =
    rule.assignee.kind === 'role'
      ? { kind: 'role', name: roles[rule.assignee.roleId] ?? rule.assignee.roleId }
      : rule.assignee.kind === 'field'
        ? { kind: 'field', name: label(rule.assignee.fieldId) }
        : { kind: rule.assignee.kind };
  return {
    on: rule.on,
    title,
    who,
    when: rule.when.all.map((group) => group.any.map((one) => ({ field: label(one.fieldId), op: one.op, values: one.values.map((held) => value(one.fieldId, held)) }))),
    due: { from: rule.due?.from === undefined ? null : label(rule.due.from), days: rule.due?.days ?? 0 },
  };
};

/** Two rules that ask the same thing, apart from their ids: a card confirmed twice adds one rule. */
export const sameTaskRule = (a: TaskRuleWrite, b: TaskRuleWrite): boolean =>
  JSON.stringify({ on: a.on, when: a.when, assignee: a.assignee, title: a.title, due: a.due ?? null }) ===
  JSON.stringify({ on: b.on, when: b.when, assignee: b.assignee, title: b.title, due: b.due ?? null });
